/**
 * CaseloadReviewStep - Step 2 of the Clinician Transfer Wizard
 * Shows the transferred clinician's caseload grouped by center (informational for v1).
 */

import styled from 'styled-components';
import { Users, CheckCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import type { CaseloadByCenter } from '../../services/api/locationTransferService';

// ==================== Styled Components ====================

const StepRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.info.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.info.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.status.info.text};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const InfoBannerText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.info.text};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
`;

const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CenterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const CenterName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ClientCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: auto;
`;

const ClientList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ClientRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.status.success.text};
    flex-shrink: 0;
  }
`;

const ClientName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

// ==================== Props ====================

interface CaseloadReviewStepProps {
  newCenterName: string;
  caseload: CaseloadByCenter[];
  onComplete: () => void;
}

// ==================== Component ====================

export const CaseloadReviewStep = ({
  newCenterName,
  caseload,
  onComplete,
}: CaseloadReviewStepProps) => {
  const totalClients = caseload.reduce((sum, group) => sum + group.clients.length, 0);

  return (
    <StepRoot>
      <InfoBanner>
        <CheckCircle />
        <InfoBannerText>
          This clinician has been transferred to <strong>{newCenterName}</strong>. Review their
          client assignments below. Client reassignments can be managed individually through the
          client transfer workflow.
        </InfoBannerText>
      </InfoBanner>

      {totalClients === 0 ? (
        <EmptyState>
          <Users />
          <EmptyStateText>This clinician has no active client assignments.</EmptyStateText>
        </EmptyState>
      ) : (
        caseload.map((group) => (
          <CenterSection key={group.centerId}>
            <CenterHeader>
              <Users />
              <CenterName>{group.centerName}</CenterName>
              <ClientCount>
                {group.clients.length} client{group.clients.length !== 1 ? 's' : ''}
              </ClientCount>
            </CenterHeader>
            <ClientList>
              {group.clients.map((client) => (
                <ClientRow key={client.id}>
                  <CheckCircle />
                  <ClientName>
                    {client.firstName} {client.lastName}
                  </ClientName>
                </ClientRow>
              ))}
            </ClientList>
          </CenterSection>
        ))
      )}

      <Actions>
        <Button variant="gradient" rightIcon={<ChevronRight />} onClick={onComplete}>
          Done — Return to Center
        </Button>
      </Actions>
    </StepRoot>
  );
};

export default CaseloadReviewStep;
